import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Rocket, TrendUp, Bank, ChartLineUp, Globe, Handshake, Sparkle, ArrowRight, ArrowLeft, CheckCircle, Spinner, Download, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

const ICON_MAP = {
  rocket: Rocket, trending: TrendUp, building: Bank,
  "chart-up": ChartLineUp, globe: Globe, handshake: Handshake,
};
const ACCENT_MAP = {
  teal: "bg-teal-soft text-teal",
  gold: "bg-gold-soft text-gold",
  navy: "bg-secondary text-primary",
};

export default function WorkspaceDetail() {
  const { id } = useParams();
  const [ws, setWs] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [showLauncher, setShowLauncher] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const aliveRef = useRef(true);
  const pollRef = useRef(null);
  const consecutiveFailsRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const [w, c] = await Promise.all([
        api.get(`/workspaces/${id}`),
        api.get("/workflows/catalog"),
      ]);
      if (!aliveRef.current) return;
      setWs(w.data.workspace);
      setWorkflows(w.data.workflows);
      setDeliverables(w.data.deliverables);
      setCatalog(c.data.workflows);
      setLoadError(null);
      consecutiveFailsRef.current = 0;
    } catch (e) {
      const status = e.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        setLoadError({
          status,
          message:
            status === 404
              ? "This workspace does not exist or has been deleted."
              : status === 403
              ? "You do not have permission to view this workspace."
              : "Your session has expired. Please sign in again.",
        });
        return;
      }
      consecutiveFailsRef.current += 1;
      // First load — show error immediately. Subsequent polls — only after 3 fails.
      if (!ws || consecutiveFailsRef.current >= 3) {
        setLoadError({
          status: status || 0,
          message: "Unable to reach the workspace service. Check your connection and retry.",
        });
        toast.error("Workspace data refresh failed");
      }
    }
  }, [id, ws]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    pollRef.current = setInterval(load, 6000);
    return () => { aliveRef.current = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, [id, load]);

  const handleRetry = () => {
    setLoadError(null);
    consecutiveFailsRef.current = 0;
    load();
    if (!pollRef.current) pollRef.current = setInterval(load, 6000);
  };

  // ---- Error state (only if we never loaded successfully) ----
  if (loadError && !ws) {
    return (
      <div className="p-8 lg:p-12 max-w-2xl" data-testid="workspace-detail-error" role="alert">
        <Link to="/workspaces" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft size={12} weight="bold" /> All workspaces
        </Link>
        <div className="mt-6 inline-flex items-center gap-2 text-overline text-rose-600">
          <WarningCircle size={14} weight="bold" /> Workspace unavailable
        </div>
        <h1 className="font-display text-3xl tracking-tight mt-2">{loadError.message}</h1>
        <div className="mt-7 flex flex-wrap gap-3">
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-primary/90"
            data-testid="workspace-detail-retry"
          >
            <ArrowsClockwise size={14} weight="bold" /> Retry
          </button>
          <Link to="/workspaces" className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary">
            All workspaces
          </Link>
        </div>
      </div>
    );
  }

  // ---- Loading state ----
  if (!ws) {
    return (
      <div className="p-8 lg:p-12" data-testid="workspace-detail-loading" role="status" aria-live="polite">
        <div className="inline-flex items-center gap-3">
          <Spinner size={20} weight="bold" className="animate-spin text-primary" />
          <div className="font-display text-2xl tracking-tight">Loading workspace…</div>
        </div>
      </div>
    );
  }

  const exportWorkspace = async (fmt) => {
    try {
      const token = localStorage.getItem("orixa_token") || "";
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      const resp = await fetch(`${API}/workspaces/${id}/export/${fmt}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safe = (ws.name || "workspace").replace(/[^a-z0-9]+/gi, "-").slice(0, 60);
      link.href = url; link.download = `Orixa-Workspace-${safe}.${fmt}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="workspace-detail-page">
      <Link to="/workspaces" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> Workspaces
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-overline text-primary mb-1">Workspace</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="workspace-title">{ws.name}</h1>
          {ws.description && <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">{ws.description}</p>}
          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground font-mono">
            <span>{workflows.length} workflows</span>
            <span>{deliverables.length} deliverables</span>
            <span>Created {ws.created_at?.slice(0, 10)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="workspace-actions">
          <div className="inline-flex items-center gap-1 border border-border bg-card rounded-lg overflow-hidden" data-testid="workspace-export-group">
            <button onClick={() => exportWorkspace("pdf")} className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm hover:bg-secondary font-medium" data-testid="workspace-export-pdf">
              <Download size={13} /> PDF
            </button>
            <div className="h-5 w-px bg-border" />
            <button onClick={() => exportWorkspace("docx")} className="px-3 sm:px-4 py-2.5 text-xs sm:text-sm hover:bg-secondary font-medium" data-testid="workspace-export-docx">DOCX</button>
            <div className="h-5 w-px bg-border" />
            <button onClick={() => exportWorkspace("pptx")} className="px-3 sm:px-4 py-2.5 text-xs sm:text-sm hover:bg-secondary font-medium" data-testid="workspace-export-pptx">PPTX</button>
          </div>
          <button
            onClick={() => setShowLauncher((s) => !s)}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 card-elev"
            data-testid="add-workflow-btn"
          >
            <Plus weight="bold" size={14} /> Add workflow
          </button>
        </div>
      </div>

      {showLauncher && (
        <div className="mb-10" data-testid="add-workflow-grid">
          <div className="text-overline text-muted-foreground mb-3">Choose a workflow to add</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {catalog.map((c) => {
              const Icon = ICON_MAP[c.icon] || Sparkle;
              return (
                <Link
                  key={c.id}
                  to={`/workflows/${c.id}?workspace=${id}`}
                  className="bg-background hover:bg-card transition-colors p-5 flex items-start gap-3"
                  data-testid={`add-workflow-${c.id}`}
                >
                  <div className={`h-9 w-9 rounded-md grid place-items-center flex-shrink-0 ${ACCENT_MAP[c.accent] || ACCENT_MAP.navy}`}>
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.tagline}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Active workflows */}
      <div className="mb-12">
        <div className="text-overline text-muted-foreground mb-3">Workflows ({workflows.length})</div>
        {workflows.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-10 text-center" data-testid="workspace-empty-workflows">
            <Sparkle size={28} className="text-muted-foreground mx-auto mb-3" />
            <div className="text-sm text-muted-foreground">No workflows yet. Click <span className="font-semibold text-foreground">Add workflow</span> above to begin.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="workspace-workflows-list">
            {workflows.map((w) => {
              const Icon = ICON_MAP[w.icon] || Sparkle;
              const completed = (w.steps || []).filter((s) => s.status === "completed").length;
              const total = (w.steps || []).length;
              return (
                <Link key={w.id} to={`/workflows/run/${w.id}`}
                  className="bg-card border border-border rounded-xl p-5 hover:card-elev transition-shadow flex items-start gap-3"
                  data-testid={`workflow-card-${w.id}`}
                >
                  <div className={`h-10 w-10 rounded-lg grid place-items-center flex-shrink-0 ${ACCENT_MAP[w.accent] || ACCENT_MAP.navy}`}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-sm">{w.label}</div>
                      {w.status === "completed" && <CheckCircle size={16} weight="fill" className="text-teal" />}
                      {w.status === "running" && <Spinner size={14} className="animate-spin text-primary" />}
                      {w.status === "failed" && <span className="text-xs text-rose-500 font-semibold">Failed</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {completed}/{total} deliverables · {w.progress}%
                    </div>
                    <div className="h-1 bg-secondary rounded-full overflow-hidden mt-2">
                      <div className={`h-full transition-all ${w.status === "failed" ? "bg-rose-500" : "bg-primary"}`}
                        style={{ width: `${w.progress}%` }} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Deliverables */}
      <div>
        <div className="text-overline text-muted-foreground mb-3">Recent deliverables</div>
        {deliverables.length === 0 ? (
          <div className="text-sm text-muted-foreground">No deliverables generated yet.</div>
        ) : (
          <div className="border border-border rounded-xl divide-y divide-border bg-card" data-testid="workspace-deliverables-list">
            {deliverables.slice(0, 10).map((d) => (
              <div key={d.id} className="p-4 flex items-start gap-3 hover:bg-secondary/40 transition-colors">
                <FileText size={18} weight="duotone" className="text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <Link to={`/workflows/run/${d.workflow_run_id}`} className="font-medium text-sm hover:text-primary">{d.label}</Link>
                  {d.summary && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.summary}</p>}
                </div>
                <div className="text-[11px] text-muted-foreground font-mono flex-shrink-0">{d.created_at?.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
